import * as React from "react";
import { MailTemplate } from "../../../../application/domain/procedures/default-mail-cfg-entity";
import { MailTemplateValidator } from "../../../../application/domain/procedures/procedure-mail-cfg-entity";
import Mustache from "mustache";
import classNames from "classnames";
import { useModal } from "../../../../utils/custom-hooks";
import { UpdateButton } from "./update-button";
import { ProcedureEntity } from "../../../../application/domain/procedures/procedure-entity";
import OutsideClickHandler from "@graffer-inc/react-outside-click-handler";
import { useState } from "react";
import { BaseModal } from "../../../atoms/base-modal";
import { InputWithButton } from "../../../molecules/input-with-button";
import { verifyEmail } from "../../../../utils/email-utils";
import { SecondaryButton } from "@graffer-inc/console-design-kit";
import { DeprecatedTooltip } from "../../../atoms/deprecated-tooltip";

export type AttributesMappingType = {
  tag: string;
  alias: string;
  description: string;
  tooltip?: string;
  titleOnly?: boolean;
  bodyOnly?: boolean;
};

const { validateBody, validateTitle } = MailTemplateValidator;

export const makeParseTemplate = (sampleAttributes: object) => {
  return (tmpl: string): string => {
    try {
      return Mustache.render(
        tmpl,
        sampleAttributes,
        {},
        {
          // tag is the same with golang logic used in mail body rendering
          tags: ["{{.", "}}"],
          escape: (value: string) => value,
        },
      );
    } catch (e) {
      return tmpl;
    }
  };
};

export const MailTemplateEditor: React.FC<{
  explanation: string;
  procedure: ProcedureEntity;
  initialTmpl: MailTemplate;
  footer: string;
  attribute: Array<AttributesMappingType>;
  sampleAttributes: object;
  isValidCfg: boolean;
  onTestSend: (
    email: string,
    template: MailTemplate,
    sampleApplication: object,
  ) => void;
  onSave: () => void;
  onTemplateEdit: (tmpl: MailTemplate) => void;
}> = ({
  explanation,
  procedure,
  attribute,
  sampleAttributes,
  initialTmpl,
  footer,
  isValidCfg,
  onTestSend,
  onSave,
  onTemplateEdit,
}) => {
  const titleBlockHeight = 100;
  const [value, setValue] = React.useState<MailTemplate>(initialTmpl);
  const testSendModal = useModal(false);

  const convertToAlias = (tmpl: string): string => {
    attribute.forEach((mapping) => {
      const regexp = new RegExp(mapping.tag, "g");
      tmpl = tmpl.replace(regexp, mapping.alias);
    });
    return tmpl;
  };

  const convertToTags = (tmpl: string): string => {
    attribute.forEach((mapping) => {
      const regexp = new RegExp(mapping.alias, "g");
      tmpl = tmpl.replace(regexp, mapping.tag);
    });
    return tmpl;
  };

  const onEditTitle = (title: string) => {
    const rawTitle = convertToTags(title);
    const template: MailTemplate = {
      title: rawTitle,
      body: value.body,
    };
    setValue(template);
    onTemplateEdit(template);
  };

  const onEditBody = (body: string) => {
    const rawBody = convertToTags(body);
    const template: MailTemplate = {
      title: value.title,
      body: rawBody,
    };
    setValue(template);
    onTemplateEdit(template);
  };

  const parseTemplate = makeParseTemplate(sampleAttributes);

  const titleError = validateTitle(value.title);
  const bodyError = validateBody(value.body);

  return (
    <>
      <div className="columns">
        <div className="column is-8">
          <div>{explanation}</div>
        </div>
        {!procedure.isArchived() && (
          <div className="column is-4 has-text-right buttons">
            <SecondaryButton text={"テスト送信"} onClick={testSendModal.open} />
            <UpdateButton
              procedure={procedure}
              canModifyAfterPublish={true}
              canModifyCustomTag={true}
              onClick={onSave}
              disabled={!isValidCfg}
            />
          </div>
        )}
      </div>
      <div className="columns">
        <div className="column is-6 box" style={{ padding: "0 16px" }}>
          <div
            style={{
              borderBottom: "1px solid rgba(128,128,128)",
              padding: "8px 0",
              marginBottom: "16px",
            }}
          >
            <label className="label">文面編集</label>
          </div>
          <div className="field" style={{ height: titleBlockHeight }}>
            <div
              className="columns mx-0 mt-2 mb-1"
              style={{ lineHeight: "1.8em" }}
            >
              <label className="label column is-narrow m-0 p-0">件名</label>
              <div className="column py-0 px-1">
                <AddDropDownMenu
                  attributeMappings={attribute.filter((m) => !m.bodyOnly)}
                  onClick={(appAttrTag: string) =>
                    onEditTitle(`${appAttrTag}${value.title}`)
                  }
                />
              </div>
            </div>
            <div className="control">
              <input
                className={classNames("input", {
                  "is-danger": !!titleError,
                })}
                value={convertToAlias(value.title)}
                onChange={(e) => onEditTitle(e.target.value)}
              />
            </div>
            {titleError && <p className="help is-danger">{titleError}</p>}
          </div>
          <div className="field">
            <div className="columns m-0 mb-1" style={{ lineHeight: "1.8em" }}>
              <label className="label column is-narrow m-0 p-0">本文</label>
              <div className="column py-0 px-1">
                <AddDropDownMenu
                  attributeMappings={attribute.filter((m) => !m.titleOnly)}
                  onClick={(appAttrTag: string) =>
                    onEditBody(`${appAttrTag}${value.body}`)
                  }
                />
              </div>
            </div>
            <div className="control">
              <textarea
                className={classNames("textarea", {
                  "is-danger": !!bodyError,
                })}
                rows={14}
                value={convertToAlias(value.body)}
                onChange={(e) => onEditBody(e.target.value)}
              />
            </div>
            {bodyError && <p className="help is-danger">{bodyError}</p>}
          </div>
          <div className="field">
            <label className="label">署名</label>
            <div className="control">
              <textarea
                className="textarea"
                defaultValue={convertToAlias(footer)}
                disabled={true}
                rows={11}
              />
            </div>
          </div>
        </div>
        <div className="column is-6" style={{ padding: "0 16px" }}>
          <div
            style={{
              borderBottom: "1px solid rgba(128,128,128)",
              padding: "8px 0",
              marginBottom: "16px",
            }}
          >
            <label className="label">サンプルプレビュー</label>
          </div>
          <div className="field" style={{ height: titleBlockHeight }}>
            <label className="label">件名</label>
            <div className="control">
              <input
                className="input"
                value={parseTemplate(value.title)}
                disabled={true}
              />
            </div>
          </div>
          <div className="field">
            <label className="label">本文</label>
            <div className="control">
              <textarea
                className="textarea"
                rows={24}
                value={`${parseTemplate(value.body)}\n\n${parseTemplate(
                  footer,
                )}`}
                disabled={true}
              />
            </div>
          </div>
        </div>
      </div>
      <BaseModal
        title={"メールのテスト送信"}
        isActive={testSendModal.isActive}
        onClose={testSendModal.close}
      >
        <section className="modal-card-body">
          <div className="field">
            <div>
              編集中の文面を利用して指定のアドレスにテストメールを送信することが出来ます。テストメールの送信先メールアドレスを入力してください。
            </div>
            <InputWithButton
              onSubmit={async (email) => {
                await onTestSend(email, value, sampleAttributes);
                testSendModal.close();
              }}
              type="email"
              btnName="送信"
              placeholder="send-test@example.com"
              disableFunc={(value: string) => !verifyEmail(value)}
            />
          </div>
        </section>
        <footer className="modal-card-foot">
          <button
            className="button is-failure is-fullwidth"
            onClick={testSendModal.close}
          >
            キャンセル
          </button>
        </footer>
      </BaseModal>
    </>
  );
};
const AddDropDownMenu: React.FC<{
  attributeMappings: Array<AttributesMappingType>;
  onClick: (appAttrTag: string) => void;
}> = ({ attributeMappings, onClick }) => {
  const [isActive, setIsActive] = useState<boolean>(false);

  return (
    <>
      <OutsideClickHandler onOutsideClick={() => setIsActive(false)}>
        <div
          className={classNames("dropdown", "is-up", {
            "is-active": isActive,
          })}
        >
          <div className="dropdown-trigger">
            <button
              className="button is-small is-rounded"
              onClick={() => setIsActive(!isActive)}
            >
              <span className="icon is-small">
                <i className="fas fa-plus"></i>
              </span>
              <span>申請情報を追加</span>
            </button>
          </div>
          <div className="dropdown-menu" role="menu">
            <div
              className="dropdown-content column"
              style={{
                maxHeight: "296px",
                width: "540px",
              }}
            >
              <div className="field">
                <p>以下の申請書情報をメール文面に挿入することが出来ます。</p>
              </div>
              <table
                className="table"
                style={{
                  display: "block",
                  overflow: "hidden scroll",
                  height: "216px",
                }}
              >
                <thead>
                  <tr>
                    <th>項目説明</th>
                    <th>記述</th>
                    <th aria-label="先頭に追加"></th>
                  </tr>
                </thead>
                <tbody>
                  {attributeMappings.map((m) => (
                    <tr key={m.tag}>
                      <td>
                        {m.description}
                        {m.tooltip && <DeprecatedTooltip text={m.tooltip} />}
                      </td>
                      <td>{m.alias}</td>
                      <td>
                        <SecondaryButton
                          text={"先頭に追加"}
                          onClick={() => onClick(m.alias)}
                          isSmall={true}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </OutsideClickHandler>
    </>
  );
};
